import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import { api } from "../scripts/api";
import CardComponent from "../components/CardComponent";
import { RefreshCw, MoreVertical } from "lucide-react";

const SORT_KEYS = [
  { k: "unit_status", label: "Status" },
  { k: "unit_category", label: "Category" },
  { k: "color_code", label: "Color Code" },
  { k: "rowo", label: "RO/WO" },
  { k: "stock_po", label: "Stock PO" },
  { k: "customer", label: "Customer" },
];

const CATEGORY_OPTIONS = [
  { v: "", label: "(blank)" },
  { v: "landfill", label: "Landfill" },
  { v: "sewer", label: "Sewer equipment" },
  { v: "sweeper", label: "Sweeper" },
  { v: "refuse", label: "Refuse" },
  { v: "municipal", label: "Municipal" },
  { v: "mrr", label: "MRR" },
  { v: "rail", label: "rail" },
];

const INVENTORY_CATEGORIES = [
  { key: "all", label: "All Inventory" },
  { key: "demo", label: "Demo" },
  { key: "stock", label: "Stock" },
  { key: "rental", label: "Rental" },
  { key: "sold", label: "Sold" },
];

function norm(v) {
  return String(v ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}
function buildSearchIndex(row) {
  const parts = [];
  for (const v of Object.values(row || {})) {
    if (v == null) continue;
    if (typeof v === "object") continue;
    const s = norm(v);
    if (s) parts.push(s);
  }
  return parts.join(" | ");
}
function valStr(v) {
  if (v === null || v === undefined) return "";
  return String(v).toLowerCase();
}
function getCustomerName(r) {
  return (r.customer || "").toString().toLowerCase();
}
function isTruthyFlag(v) {
  const s = String(v ?? "").toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function StatMiniSmall({ title, value, sub }) {
  return (
    <div className="scan-tile" style={{ textAlign: "left", padding: 10, minHeight: 0 }}>
      <div className="scan-title" style={{ fontSize: 11, opacity: 0.8, letterSpacing: 0.2 }}>
        {title}
      </div>
      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 4, lineHeight: 1.1 }}>{value}</div>
      {sub ? (
        <div className="scan-sub" style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function HoverActions({ onMoveUnit, onOther1, onOther2 }) {
  return (
    <div
      className="inv-hover-actions"
      style={{
        position: "absolute",
        top: 10,
        right: 10,
        display: "none",
        alignItems: "center",
        gap: 6,
        padding: 6,
        borderRadius: 12,
        background: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(6px)",
        zIndex: 5,
      }}
    >
      <button className="btn-outline" style={{ padding: "6px 10px" }} onClick={onMoveUnit} type="button">
        Move Unit
      </button>
      <button className="btn-outline" style={{ padding: "6px 10px" }} onClick={onOther1} type="button">
        Other
      </button>
      <button className="btn-outline" style={{ padding: "6px 10px" }} onClick={onOther2} type="button">
        Other
      </button>
      <span style={{ opacity: 0.7, paddingLeft: 2 }}>
        <MoreVertical size={16} />
      </span>
    </div>
  );
}

export default function InventoryDesktop() {
  const table = "all_inventory";

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(() => localStorage.getItem("inv_q") || "");
  const [cat, setCat] = useState(() => localStorage.getItem("inv_cat") || "__all__");
  const [sortKey, setSortKey] = useState(() => localStorage.getItem("inv_sort_key") || "color_code");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("inv_sort_dir") || "asc");

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [summary, setSummary] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  // notes summary: { [id]: { total, unread } }
  const [noteSummary, setNoteSummary] = useState({});
  const notesDebounceRef = useRef(null);

  useEffect(() => localStorage.setItem("inv_cat", cat), [cat]);
  useEffect(() => localStorage.setItem("inv_q", q), [q]);
  useEffect(() => {
    localStorage.setItem("inv_sort_key", sortKey);
    localStorage.setItem("inv_sort_dir", sortDir);
  }, [sortKey, sortDir]);

  const load = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const res = await api.get(`/${table}`);
      const data = res?.data;
      const list = Array.isArray(data) ? data : [];
      setRows(list);
    } catch (e) {
      console.error("Load failed:", e);
      setRows([]);
      setErrMsg(e?.response?.data?.error || "Failed to load inventory.");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await api.get(`/reports/summary`, { params: { table } });
      setSummary(data || null);
    } catch {
      setSummary(null);
    }
  };

  useEffect(() => {
    load();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowsIndexed = useMemo(() => rows.map((r) => ({ ...r, __idx: buildSearchIndex(r) })), [rows]);

  const filtered = useMemo(() => {
    const terms = norm(q).split(" ").filter(Boolean);
    let arr = rowsIndexed;

    if (cat !== "__all__") arr = arr.filter((r) => norm(r.unit_category) === norm(cat));

    if (activeCategory !== "all") {
      if (activeCategory === "demo") arr = arr.filter((r) => isTruthyFlag(r.is_demo));
      else if (activeCategory === "stock") arr = arr.filter((r) => isTruthyFlag(r.is_stock));
      else if (activeCategory === "rental") arr = arr.filter((r) => isTruthyFlag(r.is_rental));
      else if (activeCategory === "sold") arr = arr.filter((r) => norm(r.unit_status) === "sold");
    }

    if (terms.length) {
      arr = arr.filter((r) => {
        const hay = r.__idx || "";
        for (const t of terms) if (!hay.includes(t)) return false;
        return true;
      });
    }

    const dir = sortDir === "desc" ? -1 : 1;

    return [...arr].sort((a, b) => {
      let va = "";
      let vb = "";

      if (sortKey === "customer") {
        va = getCustomerName(a);
        vb = getCustomerName(b);
      } else if (sortKey === "color_code") {
        va = Number(a.color_code ?? 0);
        vb = Number(b.color_code ?? 0);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      } else {
        va = valStr(a[sortKey]);
        vb = valStr(b[sortKey]);
      }

      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rowsIndexed, q, cat, sortKey, sortDir, activeCategory]);

  const refreshNotesSummary = async (list) => {
    const ids = (list || [])
      .map((r) => r.id)
      .filter((x) => x != null)
      .slice(0, 600);

    if (!ids.length) {
      setNoteSummary({});
      return;
    }

    try {
      const { data } = await api.get("/api/inventory/notes/summary", { params: { ids: ids.join(",") } });
      setNoteSummary(data?.byId || {});
    } catch (e) {
      // don’t block the page if notes fails
      setNoteSummary({});
    }
  };

  // refresh notes summary when filtered changes (debounced)
  useEffect(() => {
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(() => {
      refreshNotesSummary(filtered);
    }, 250);

    return () => notesDebounceRef.current && clearTimeout(notesDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const filterContent = (
    <div>
      <div className="dash-section">Search</div>
      <input
        className="input"
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ width: "100%", marginTop: 8 }}
      />

      <div className="dash-section">Category</div>
      <select className="input" style={{ width: "100%", marginTop: 8 }} value={cat} onChange={(e) => setCat(e.target.value)}>
        <option value="__all__">All categories</option>
        {CATEGORY_OPTIONS.map((o) => (
          <option key={o.v} value={o.v}>
            {o.label}
          </option>
        ))}
      </select>

      <div className="dash-section">Inventory Type</div>
      <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
        {INVENTORY_CATEGORIES.map(({ key, label }) => (
          <button
            key={key}
            className={activeCategory === key ? "btn" : "btn-outline"}
            style={{ width: "100%", justifyContent: "flex-start" }}
            onClick={() => setActiveCategory(key)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="dash-section">Sort</div>
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        <select className="input" style={{ width: "100%" }} value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          {SORT_KEYS.map((s) => (
            <option key={s.k} value={s.k}>
              {s.label}
            </option>
          ))}
        </select>
        <select className="input" style={{ width: "100%" }} value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
          <option value="asc">Asc</option>
          <option value="desc">Desc</option>
        </select>
      </div>

      <div className="dash-section">Actions</div>
      <button
        className="btn-outline"
        style={{ width: "100%", marginTop: 8 }}
        onClick={() => {
          load();
          loadSummary();
          refreshNotesSummary(filtered);
        }}
        disabled={loading}
        type="button"
      >
        <RefreshCw size={16} style={{ marginRight: 8 }} />
        Refresh
      </button>

      {errMsg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {errMsg}
        </div>
      ) : null}
    </div>
  );

  const top = (
    <div
      className="scan-grid"
      style={{
        marginTop: 0,
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 10,
      }}
    >
      <StatMiniSmall title="Total" value={String(summary?.total ?? rows.length)} sub="records" />
      <StatMiniSmall title="Alerts" value={String(summary?.flags?.alerts ?? "-")} sub="flagged" />
      <StatMiniSmall
        title="Stock / Demo / Rental"
        value={
          summary?.flags
            ? `${summary.flags.is_stock ?? 0} / ${summary.flags.is_demo ?? 0} / ${summary.flags.is_rental ?? 0}`
            : "-"
        }
        sub="flags"
      />
      <StatMiniSmall title="Showing" value={String(filtered.length)} sub="filtered" />
    </div>
  );

  // Placeholder: you’ll plug this into your NotesWindow component next
  const openNotesFor = (r) => {
    alert(`Open notes for inventory #${r.id} (wire NotesWindow next)`);
  };

  return (
    <div className="container">
      <style>{`
        .inv-card:hover .inv-hover-actions { display: inline-flex; }
      `}</style>

      <DashboardShell left={filterContent} top={top}>
        {loading && <div className="muted">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="muted">No records.</div>}

        <div className="col">
          {filtered.map((r) => {
            const ns = noteSummary?.[r.id] || { total: 0, unread: 0 };

            return (
              <div key={r.id} className="card inv-card" style={{ minWidth: 0, position: "relative" }}>
                <HoverActions
                  onMoveUnit={() => alert(`Move Unit: #${r.id}`)}
                  onOther1={() => alert(`Other action 1: #${r.id}`)}
                  onOther2={() => alert(`Other action 2: #${r.id}`)}
                />

                <div className="row" style={{ alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontWeight: 800 }}>
                    #{r.id}
                    {r.rowo ? ` • RO/WO ${r.rowo}` : ""}
                    {r.stock_po ? ` • Stock ${r.stock_po}` : ""}
                    {r.customer ? ` • ${r.customer}` : ""}
                  </span>

                  <div className="muted" style={{ whiteSpace: "nowrap" }}>
                    cc:{String(r.color_code ?? "").trim() || "-"} • stock:{isTruthyFlag(r.is_stock) ? "1" : "0"} • demo:
                    {isTruthyFlag(r.is_demo) ? "1" : "0"} • rental:{isTruthyFlag(r.is_rental) ? "1" : "0"}
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <CardComponent
                    record={r}
                    table="all_inventory"
                    notesTotal={ns.total}
                    notesUnread={ns.unread}
                    onOpenNotes={() => openNotesFor(r)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </DashboardShell>
    </div>
  );
}
